import { animate, animation, keyframes, style } from "@angular/animations";

/*

 HOW THIS WORKS:

 If we want to let an element fly from somewhere on the screen to somewhere else,
 we need its absolute source position and the destination. Then we can just switch
 the element to fixed positioning and animate its "left" and "top" properties.

 Since the position can't known at compile time, we need a parametrized animation
 (documented here: https://angular.io/guide/reusable-animations) to which we supply
 the start and end tuples.

 Then we can just use getBoundingBoxRect() to get the absolute position of the source
 and dest elements and animate the flying element accordingly. Note that the flying
 element itself should neither be the source nor destination element and is not visible
 by default.

 */


/**
 * Create a parametrized animation which makes an element "fly" from start to end
 * coordinates, changing opacity and color. The function is evaluated at compile
 * time and returns the animation itself, which can be put into a transition:
 *
 * @Component({
 *  ...
 *  animations: [
 *    trigger('addToCart', [
 *      transition('off => on',
 *        useAnimation(createFlyToAnimation({startColor: '#0000ff', endColor: '#ff0000'}))
 *      )
 *    ])
 *  ]
 *})
 *
 * @param startColor
 * @param endColor
 * @param timing
 */
export function createFlyToAnimation({timing='300ms ease-in-out'}) {
  return animation([
    style({
      opacity: 1,
      display: 'block',
      position: 'fixed',
      left: '{{ startX }}px',
      top: '{{ startY }}px',
      zIndex: 10000,
      backgroundColor: '{{ startBG }}'
    }),
    animate(timing,
      keyframes([
        style({
          opacity: 0.8,
          backgroundColor: '{{ endBG }}',
          left: '{{ endX }}px',
          top: '{{ startY }}px',
          offset: 0.2
        }),
        style({
          opacity: 0.2,
          backgroundColor: '{{ endBG }}',
          left: '{{ endX }}px',
          top: '{{ endY }}px',
          offset: 1
        })
      ])
    )
  ], {
    params:
      {
        startX:0,
        startY: 0,
        endX: 0,
        endY: 0
      }
  })
}

/**
 * getFlyToAnimationParams calculates the actual start/end parameters for the
 * "fly" animation. We just look up those elements through getElementById and
 * get their clientRect through CSSOM getBoundingClientRect().
 *
 * Usage:
 *
 * @Component({...})
 * export class MyComponent {
 *   ...
 *   doAnimation() {
 *     return {
 *       value: this.animationState,
 *       params: getFlyToAnimationParams('src-element-id', 'dest-element-id')
 *     }
 *   }
 *   ...
 * }
 *
 * <span id="src-element-id" [@addToCart]="doAnimation()">...</span>
 *
 * @param srcId     DOM id of the src element
 * @param targetId  DOM id of the dest element
 */
export function getFlyToAnimationParams(srcId: string, targetId: string) {

  const srcElement = document.getElementById(srcId),
    destElement = document.getElementById(targetId);

  if (srcElement && destElement) {
    const srcRect = srcElement.getBoundingClientRect(),
          destRect = destElement.getBoundingClientRect(),
          startBG = window.getComputedStyle(srcElement.parentElement).backgroundColor,
          endBG = window.getComputedStyle(destElement).backgroundColor;
    return {
      startX: srcRect.left,
      startY: srcRect.top,
      endX: destRect.left + (destRect.width/2),
      endY: destRect.top,
      startBG: startBG,
      endBG: endBG
    }
  } else {
    // elements are not present yet on the screen
    return {}
  }
}
